import { format } from "date-fns";
import { useAtom } from "jotai";
import { Check, Palette, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import {
  designPageAtom,
  designSearchAtom,
  designStatusFilterAtom,
  designWidgetFilterAtom,
  designFiltersAtom,
} from "../atoms/designModerationAtoms";
import {
  useDesignsList,
  useModerateDesign,
} from "../hooks/useDesignModeration";
import { DESIGN_WIDGET_TYPES } from "../types";
import type {
  Design,
  DesignModerationStatus,
  DesignWidgetType,
} from "../types";
import DesignPreview from "./DesignPreview";
import DesignStatusBadge from "./DesignStatusBadge";

const STATUS_TABS: DesignModerationStatus[] = [
  "pending",
  "approved",
  "rejected",
];

const shortId = (id?: string | null) =>
  !id ? "—" : id.length > 12 ? `${id.slice(0, 8)}…` : id;

const DesignModerationPage = () => {
  const { t } = useTranslation("common");
  const [statusFilter, setStatusFilter] = useAtom(designStatusFilterAtom);
  const [widgetFilter, setWidgetFilter] = useAtom(designWidgetFilterAtom);
  const [search, setSearch] = useAtom(designSearchAtom);
  const [page, setPage] = useAtom(designPageAtom);
  const [filters] = useAtom(designFiltersAtom);

  const { data, isLoading, isError, error } = useDesignsList(filters);
  const moderate = useModerateDesign();

  const [selected, setSelected] = useState<Design | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);

  const designs = data?.data ?? [];
  const total = data?.total ?? 0;
  const limit = filters.limit ?? 24;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

  const closeReview = () => {
    setSelected(null);
    setRejecting(false);
    setRejectNote("");
  };

  const handleApprove = (design: Design) => {
    moderate.mutate(
      { id: design.id, body: { decision: "approved" } },
      {
        onSuccess: () => {
          setFeedback({
            kind: "success",
            message: t("DESIGNS.approvedToast", "Design approved."),
          });
          closeReview();
        },
        onError: (err) =>
          setFeedback({
            kind: "error",
            message:
              err instanceof Error
                ? err.message
                : t("DESIGNS.moderationError", "Could not save decision."),
          }),
      },
    );
  };

  const handleReject = (design: Design) => {
    moderate.mutate(
      {
        id: design.id,
        body: { decision: "rejected", note: rejectNote.trim() || undefined },
      },
      {
        onSuccess: () => {
          setFeedback({
            kind: "success",
            message: t("DESIGNS.rejectedToast", "Design rejected."),
          });
          closeReview();
        },
        onError: (err) =>
          setFeedback({
            kind: "error",
            message:
              err instanceof Error
                ? err.message
                : t("DESIGNS.moderationError", "Could not save decision."),
          }),
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("DESIGNS.title", "Widget Designs")}
        subtitle={t(
          "DESIGNS.subtitle",
          "Moderation queue for widget themes submitted to the Store.",
        )}
      />

      {feedback && (
        <div
          className={
            feedback.kind === "success"
              ? "rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300"
              : "rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          }
        >
          {feedback.message}
        </div>
      )}

      <Card className="border-neutral-800 bg-neutral-900/50">
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {STATUS_TABS.map((tab) => (
                <Button
                  key={tab}
                  variant={statusFilter === tab ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setStatusFilter(tab);
                    setPage(1);
                  }}
                  className={
                    statusFilter === tab
                      ? "bg-purple-600 text-white hover:bg-purple-700"
                      : "border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  }
                >
                  {t(
                    `DESIGNS.status${tab[0].toUpperCase()}${tab.slice(1)}`,
                    tab,
                  )}
                </Button>
              ))}
            </div>

            <select
              value={widgetFilter}
              onChange={(e) => {
                setWidgetFilter(e.target.value as DesignWidgetType | "all");
                setPage(1);
              }}
              className="h-9 rounded-md border border-neutral-700 bg-neutral-800/50 px-3 text-sm text-neutral-200"
            >
              <option value="all">
                {t("DESIGNS.allWidgets", "All widgets")}
              </option>
              {DESIGN_WIDGET_TYPES.map((wt) => (
                <option key={wt} value={wt}>
                  {wt}
                </option>
              ))}
            </select>

            <div className="min-w-[200px] flex-1">
              <Input
                type="text"
                placeholder={t(
                  "DESIGNS.searchPlaceholder",
                  "Search by name or slug...",
                )}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-12">
              <LoadingOverlay />
            </div>
          ) : isError ? (
            <EmptyState
              title={t("DESIGNS.errorTitle", "Could not load designs")}
              description={
                error instanceof Error
                  ? error.message
                  : t("DESIGNS.errorDescription", "Try again in a moment.")
              }
            />
          ) : designs.length === 0 ? (
            <EmptyState
              icon={<Palette className="h-8 w-8" />}
              title={t("DESIGNS.emptyTitle", "No designs in this view")}
              description={t(
                "DESIGNS.emptyDescription",
                "There are no designs matching the current filters.",
              )}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-400">
                      {t("DESIGNS.colPreview", "Preview")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("DESIGNS.colName", "Name")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("DESIGNS.colWidget", "Widget")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("DESIGNS.colAuthor", "Author")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("DESIGNS.colStatus", "Status")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("DESIGNS.colSubmitted", "Submitted")}
                    </TableHead>
                    <TableHead className="text-right text-neutral-400">
                      {t("DESIGNS.colActions", "Actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {designs.map((design) => (
                    <TableRow
                      key={design.id}
                      className="border-neutral-800 hover:bg-neutral-800/50"
                    >
                      <TableCell>
                        <DesignPreview
                          design={design.design}
                          widgetType={design.widgetType}
                          cardWidth={design.cardWidth}
                          cardHeight={design.cardHeight}
                          className="w-28"
                        />
                      </TableCell>
                      <TableCell className="text-neutral-200">
                        <div className="flex flex-col">
                          <span className="font-medium">{design.name}</span>
                          <span className="text-xs text-neutral-500">
                            v{design.version}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="border-neutral-600 text-neutral-300"
                        >
                          {design.widgetType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {shortId(design.authorId)}
                      </TableCell>
                      <TableCell>
                        <DesignStatusBadge status={design.status} />
                      </TableCell>
                      <TableCell className="text-sm text-neutral-400">
                        {format(new Date(design.createdAt), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelected(design);
                            setRejecting(false);
                            setRejectNote("");
                          }}
                          className="text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
                        >
                          {t("DESIGNS.review", "Review")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-neutral-800 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    {t("DESIGNS.prev", "Previous")}
                  </Button>
                  <span className="text-sm text-neutral-400">
                    {t("DESIGNS.pageOf", "Page")} {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    {t("DESIGNS.next", "Next")}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) closeReview();
        }}
      >
        <DialogContent className="max-w-3xl border-neutral-800 bg-neutral-900 text-neutral-100">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selected.name}
                  <DesignStatusBadge status={selected.status} />
                </DialogTitle>
                <DialogDescription className="text-neutral-400">
                  {selected.widgetType} • v{selected.version} • {selected.slug}
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <DesignPreview
                  design={selected.design}
                  widgetType={selected.widgetType}
                  cardWidth={selected.cardWidth}
                  cardHeight={selected.cardHeight}
                />
                <div className="space-y-3 text-sm">
                  {selected.subtitle && (
                    <p className="text-neutral-300">{selected.subtitle}</p>
                  )}
                  {selected.description && (
                    <p className="whitespace-pre-wrap text-neutral-400">
                      {selected.description}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {selected.swatches.map((c, i) => (
                      <span
                        key={`${c}-${i}`}
                        className="h-5 w-5 rounded-full border border-black/40"
                        style={{ backgroundColor: c }}
                        title={c}
                      />
                    ))}
                  </div>
                  {selected.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {selected.tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="border-neutral-700 text-neutral-300"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-neutral-500">
                    {t("DESIGNS.colAuthor", "Author")}:{" "}
                    {shortId(selected.authorId)} •{" "}
                    {t("DESIGNS.installCount", "Installs")}:{" "}
                    {selected.installCount}
                  </div>
                </div>
              </div>

              {rejecting && (
                <textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  placeholder={t(
                    "DESIGNS.rejectNotePlaceholder",
                    "Reason for rejection (optional, shared with author)...",
                  )}
                  rows={3}
                  className="w-full rounded-md border border-neutral-700 bg-neutral-800/50 p-3 text-sm text-neutral-200 placeholder:text-neutral-500"
                />
              )}

              {selected.status === "pending" && (
                <DialogFooter>
                  {rejecting ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => setRejecting(false)}
                        className="border-neutral-700 text-neutral-300 hover:bg-neutral-800"
                      >
                        {t("DESIGNS.cancel", "Cancel")}
                      </Button>
                      <Button
                        onClick={() => handleReject(selected)}
                        disabled={moderate.isPending}
                        className="bg-red-600 text-white hover:bg-red-700"
                      >
                        {t("DESIGNS.confirmReject", "Confirm reject")}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => setRejecting(true)}
                        disabled={moderate.isPending}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <X className="mr-2 h-4 w-4" />
                        {t("DESIGNS.reject", "Reject")}
                      </Button>
                      <Button
                        onClick={() => handleApprove(selected)}
                        disabled={moderate.isPending}
                        className="bg-green-600 text-white hover:bg-green-700"
                      >
                        <Check className="mr-2 h-4 w-4" />
                        {t("DESIGNS.approve", "Approve")}
                      </Button>
                    </>
                  )}
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DesignModerationPage;
